import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import {
  selectCurrentAccount,
  setCurrentAccount,
} from '@/store/features/account';
import { loadCurrentUserAction } from '@/store/features/users';
import { AppSpinner } from '@/ui/app-spinner';
import { AppButton } from '@/ui/buttons';
import { AxiosError } from 'axios';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Card = styled.div`
  width: 300px;
  padding: var(--default-padding);
  background: var(--white-main);
  border-radius: var(--box-border-radius);
  background: var(--white-main);
  box-shadow: 1px 2px 14px 3px rgba(0, 0, 0, 0.14);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--medium-padding);
`;

const StyledSpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--box-border-radius);
  background: var(--beige-main);
  display: flex;
  width: 43px;
  height: 43px;
  padding: var(--xxsmall-padding);
  justify-content: center;
  align-items: center;
  gap: var(--xsmall-padding);
  flex-shrink: 0;

  svg {
    width: var(--default-padding);
    height: var(--default-padding);
    stroke-width: 4px;

    circle {
      stroke-width: 4px;
      stroke: var(--red-main) !important;
    }
  }
`;

const StyledCloseButton = styled(AppButton)`
  position: absolute;
  right: 0;
  top: 0;
  padding: 0 !important;

  width: var(--small-button-height) !important;
  height: var(--small-button-height) !important;

  &.p-button.p-button-text {
    background: transparent !important;
    color: var(--gray-darker) !important;
  }
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  color: var(--black-main);
`;

const Title = styled.div`
  font-weight: 700;
`;

const ActionButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: var(--box-border-radius);
  border: none;
  background: var(--red-dark);
  color: var(--white-main);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

type CourseCloneStatusCardProps = {
  variant: 'success' | 'loading';
  title: string;
  detail?: string;
  jobId: string;
  clonedCourseId?: string;
  targetAccountId?: string;
  isOtherAccount?: boolean;
  onRemove?: (jobId: string) => void;
};

export const CourseCloneStatusCard: React.FC<CourseCloneStatusCardProps> = ({
  variant,
  title,
  detail,
  jobId,
  clonedCourseId = '',
  targetAccountId,
  isOtherAccount,
  onRemove,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const account = useAppSelector(selectCurrentAccount);
  const toast = useToast();

  const handleAction = async () => {
    if (variant !== 'success') return;

    try {
      if (account?.id && isOtherAccount && targetAccountId !== account.id) {
        const targetAccount = await client.accounts.getAccount(targetAccountId);
        if (targetAccount) {
          dispatch(setCurrentAccount(targetAccount));
          loadCurrentUserAction(dispatch);
        }
      }

      navigate(
        RedirectPaths[RedirectPathsEnum.COURSES_CONTENT](clonedCourseId),
      );
      onRemove?.(jobId);
    } catch (e) {
      handleAxiosError(e as AxiosError, toast);
    }
  };

  return (
    <Card>
      {variant === 'success' && (
        <StyledCloseButton
          icon="pi pi-times"
          severity="success"
          type="text"
          rounded
          onClick={() => {
            onRemove?.(jobId);
          }}
        />
      )}
      <Text>
        <Title>{title}</Title>
        {detail ? <div>{detail}</div> : null}
      </Text>

      {variant === 'loading' ? (
        <StyledSpinnerContainer>
          <AppSpinner />
        </StyledSpinnerContainer>
      ) : (
        <div>
          <ActionButton type="button" onClick={handleAction}>
            <span className="pi pi-arrow-right" />
          </ActionButton>
        </div>
      )}
    </Card>
  );
};
